import React, { useEffect, useState } from 'react';
import { makeStyles, Typography, Grid, Button, Theme, Hidden } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import { getJoinTokenFromUrl, storeJoinToken } from '../../../utils/videoJoinToken';
import { redeemHandoffFromUrl } from '../../../utils/translatorHandoff';
import {
  getStoredReturnUrl,
  navigateToReturnUrl,
  persistReturnUrlFromQuery,
  shouldShowReturnAfterCall,
} from '../../../utils/returnUrl';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  mobileButton: {
    padding: '0.8em 0',
    margin: 0,
  },
  joinErrorMessage: {
    color: theme.palette.error.main,
    marginTop: '1em',
    textAlign: 'center',
  },
  postCallPanel: {
    textAlign: 'center',
    padding: '2em 1em',
    margin: '0 auto',
    maxWidth: 420,
  },
  postCallActions: {
    marginTop: '1.5em',
    display: 'flex',
    justifyContent: 'center',
  },
  returnButton: {
    textTransform: 'none',
    marginRight: '0.75em',
  },
  joinButtonsPrimary: {
    display: 'flex',
    gap: '0.75em',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  joinColumn: {
    minHeight: 280,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  setStep: (step: Steps) => void;
}

export default function DeviceSelectionScreen({ name, roomName, setStep }: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const { getToken, isFetching } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting;
  const [joinErrorMessage, setJoinErrorMessage] = useState<string | null>(null);
  const [hasReturnUrl, setHasReturnUrl] = useState(false);

  useEffect(() => {
    persistReturnUrlFromQuery();
    const syncReturnUrl = () => setHasReturnUrl(Boolean(getStoredReturnUrl()));
    syncReturnUrl();
    window.addEventListener('focus', syncReturnUrl);
    return () => window.removeEventListener('focus', syncReturnUrl);
  }, []);

  const urlParams = new URLSearchParams(window.location.search);
  const userName = urlParams.get('n') || '';
  const roomId = urlParams.get('r') || '';
  const handoffId = urlParams.get('hid') || '';

  const handleJoin = async () => {
    setJoinErrorMessage(null);
    try {
      let joinToken = getJoinTokenFromUrl();
      if (!joinToken && handoffId) {
        joinToken = await redeemHandoffFromUrl(handoffId);
      }
      if (!joinToken) {
        if (handoffId) {
          throw new Error('Could not activate this invite link. The visit opens at the scheduled appointment time.');
        }
        throw new Error('Missing video link. Open the visit from your appointment page or use the email invite link.');
      }
      storeJoinToken(joinToken);
      videoConnect(joinToken);
      process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true' && chatConnect(joinToken);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not join the video visit.';
      setJoinErrorMessage(message);
      console.error('Video join failed:', err);
    }
  };

  if (shouldShowReturnAfterCall()) {
    return (
      <div className={classes.postCallPanel} data-cy-return-banner>
        <Typography variant="h5" className={classes.gutterBottom}>
          Video visit ended
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can return to your appointment in the portal.
        </Typography>
        <div className={classes.postCallActions}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigateToReturnUrl()}
            data-cy-return-to-appointment
          >
            Return to appointment
          </Button>
        </div>
      </div>
    );
  }

  if (isFetching || isConnecting) {
    return (
      <Grid container justify="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <Typography variant="h5" className={classes.gutterBottom}>
        Join {roomId}
      </Typography>

      <Grid container justify="center">
        <Grid item md={7} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={userName} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
              <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
            </Hidden>
            <SettingsMenu mobileButtonClass={classes.mobileButton} />
          </div>
        </Grid>
        <Grid item md={5} sm={12} xs={12}>
          <Grid container direction="column" className={classes.joinColumn}>
            <div>
              <Hidden smDown>
                <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
              </Hidden>
            </div>
            <div className={classes.joinButtons}>
              {hasReturnUrl ? (
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.returnButton}
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigateToReturnUrl()}
                  data-cy-return-to-appointment-prejoin
                >
                  Appointment
                </Button>
              ) : null}
              <div className={classes.joinButtonsPrimary}>
                <Button variant="outlined" color="primary" onClick={() => setStep(Steps.roomNameStep)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  data-cy-join-now
                  onClick={() => {
                    void handleJoin();
                  }}
                  disabled={disableButtons}
                >
                  Join Now
                </Button>
              </div>
            </div>
            {joinErrorMessage ? (
              <Typography variant="body2" className={classes.joinErrorMessage}>
                {joinErrorMessage}
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
